import { MsxSuggestDocument } from 'generated/search-backend';

/**
 * Grouped search results for the unified dropdown
 */
export interface IGroupedSearchResults {
    opportunities: MsxSuggestDocument[];
    customers: ICustomerResult[];
}

/**
 * Customer result grouped from MSX data (unique by AccountName)
 */
export interface ICustomerResult {
    AccountName: string;
    Country: string;
}

/**
 * Selected customer/opportunity data after panel submission
 */
export interface IImpactDescriptionFields {
    importanceReason?: string;
    importanceOtherText?: string;
    entireDealDepends?: boolean;
    sameIssue?: boolean;
    customerImpact?: string;
    topic?: string;
    legacyOpportunityTail?: string;
}

export interface ISelectedCustomerImpact extends IImpactDescriptionFields {
    type: 'customer' | 'opportunity' | 'manual';
    opportunityId?: string;
    msxOpportunityId?: string;
    accountName: string;
    country: string;
    estimatedRevenue?: number;
    estimatedDevices?: number;
    revenueAtRisk?: number;
    devicesAffected?: number;
}

/**
 * General feedback form data
 */
export interface IGeneralFeedback {
    feedbackSource: FeedbackSource | '';
    otherText: string;
}

export interface ICustomerImpactData {
    activeTab?: CustomerImpactTab;
    selectedImpacts?: ISelectedCustomerImpact[];
    feedback?: IGeneralFeedback;
}

/**
 * Shared "Other (specify)" option key, used by both FeedbackSource and
 * ImportanceReason. Compare against this constant instead of the raw literal.
 */
export const OTHER_OPTION_KEY = 'other' as const;

/**
 * Feedback source options
 */
export type FeedbackSource =
    | 'multipleCustomerConversations'
    | 'customerAdvisoryBoard'
    | 'partnerFeedback'
    | 'fieldObservation'
    | 'other';

/**
 * Feedback source display labels
 */
export const FEEDBACK_SOURCE_LABELS: Record<FeedbackSource, string> = {
    multipleCustomerConversations: 'Multiple customer conversations',
    customerAdvisoryBoard: 'Customer Advisory Board (CAB)',
    partnerFeedback: 'Partner Feedback',
    fieldObservation: 'Field observation',
    other: 'Other',
};

/**
 * Customer importance reason options
 */
export type ImportanceReason =
    | 'strategicCustomer'
    | 'largeExistingDeployment'
    | 'broaderSegment'
    | 'earlySignal'
    | 'other';

export const IMPORTANCE_REASON_LABELS: Record<ImportanceReason, { label: string; description: string }> = {
    strategicCustomer: {
        label: 'Strategic customer',
        description: 'Key brand, reference account, or long-term partner',
    },
    largeExistingDeployment: {
        label: 'Large existing deployment',
        description: 'Impacts a customer with significant devices in use',
    },
    broaderSegment: {
        label: 'Broader segment or industry need',
        description: 'Likely relevant to many similar customers',
    },
    earlySignal: {
        label: 'Early signal of emerging requirement',
        description: 'New requirement we expect to see more frequently',
    },
    other: {
        label: 'Other',
        description: '',
    },
};

/**
 * Tab keys for the Customer Impact step. Use these constants instead of raw
 * string literals so the values stay in sync across the create-request and
 * add-customer flows.
 */
export const CUSTOMER_IMPACT_TABS = {
    CUSTOMER_OR_OPPORTUNITY: 'customerOrOpportunity',
    GENERAL_FEEDBACK: 'generalFeedback',
} as const;

/**
 * Tab selection type — null means no tab selected (initial state)
 */
export type CustomerImpactTab =
    | typeof CUSTOMER_IMPACT_TABS[keyof typeof CUSTOMER_IMPACT_TABS]
    | null;

/**
 * Panel keys for the side panels opened from the Customer Impact step.
 */
export const PANEL_TYPES = {
    ADD_NEW_CUSTOMER: 'addNewCustomer',
    CUSTOMER_IMPACT: 'customerImpact',
    OPPORTUNITY_IMPACT: 'opportunityImpact',
} as const;

/**
 * Panel type currently open — null means no panel open
 */
export type PanelType =
    | typeof PANEL_TYPES[keyof typeof PANEL_TYPES]
    | null;

/**
 * MSX search timeout in milliseconds (NFR)
 */
export const MSX_SEARCH_TIMEOUT_MS = 10000;

/**
 * Minimum characters to trigger search
 */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Country options for the dropdown
 */
export const COUNTRY_OPTIONS: string[] = [
    'United States',
    'United Kingdom',
    'Canada',
    'Germany',
    'France',
    'Japan',
    'Australia',
    'India',
    'China',
    'Brazil',
    'Mexico',
    'South Korea',
    'Netherlands',
    'Sweden',
    'Switzerland',
    'Singapore',
    'Italy',
    'Spain',
    'Norway',
    'Denmark',
    'Finland',
    'Belgium',
    'Austria',
    'New Zealand',
    'Ireland',
    'Poland',
    'Czech Republic',
    'Israel',
    'South Africa',
    'United Arab Emirates',
    'Saudi Arabia',
    'Taiwan',
    'Thailand',
    'Indonesia',
    'Malaysia',
    'Philippines',
    'Vietnam',
    'Turkey',
    'Argentina',
    'Chile',
    'Colombia',
    'Peru',
    'Egypt',
    'Nigeria',
    'Kenya',
    'Portugal',
    'Greece',
    'Romania',
    'Hungary',
    'Ukraine',
];
